import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { api, apiError, rupiah, tanggal, waktu, FILE_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Receipt, Users, FileText, CalendarDays, Bell, User, LogOut,
  Upload, Download, Wallet, ShoppingBag, GraduationCap, Pencil, X, Save,
} from "lucide-react";

const inp = "w-full rounded-xl border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const lbl = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutGrid, end: true },
  { to: "/portal/booking", label: "Booking Saya", icon: Receipt },
  { to: "/portal/rombongan", label: "Anggota Rombongan", icon: Users },
  { to: "/portal/tagihan", label: "Tagihan & Cicilan", icon: Wallet },
  { to: "/portal/dokumen", label: "Dokumen", icon: FileText },
  { to: "/portal/jadwal", label: "Jadwal & Itinerary", icon: CalendarDays },
  { to: "/portal/order", label: "Order Toko", icon: ShoppingBag },
  { to: "/e-course", label: "E-Course Saya", icon: GraduationCap },
  { to: "/portal/notifikasi", label: "Notifikasi", icon: Bell },
  { to: "/portal/profil", label: "Profil & Keamanan", icon: User },
];

export function PortalLayout({ title, children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <header className="bg-[#0A0A0A] px-6 py-4 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Royal Saudi Haramain" className="h-7 w-auto" />
            <span className="font-display text-sm font-bold">Portal Jamaah</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs sm:block">{user?.full_name}</span>
            <button onClick={logout} data-testid="portal-logout-btn" className="rounded-lg p-2 hover:bg-white/10"><LogOut size={16} /></button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 lg:flex-row lg:px-6">
        <aside className="lg:w-64 lg:shrink-0" data-testid="portal-nav">
          <nav className="flex gap-2 overflow-x-auto rounded-xl border border-[#E5DEC9] bg-white p-2 lg:flex-col lg:overflow-visible">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`portal-nav-${n.to.split("/").pop()}`}
                className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${isActive ? "bg-[#141414] text-white" : "text-[#4A4844] hover:bg-[#F4EFE6]"}`}>
                <n.icon size={15} /> {n.label}
              </NavLink>))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-bold text-[#141414] lg:text-3xl">{title}</h1>
          <div className="mt-6">{children}</div>
        </main>
      </div>
    </div>
  );
}

const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-[#E5DEC9] bg-white p-5 ${className}`} {...rest}>{children}</div>
);

const StatusBadge = ({ s }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
    ["paid", "confirmed", "approved", "verified", "documents_complete", "completed"].includes(s)
      ? "bg-[#F5F1E8] text-[#15803D]"
      : ["rejected", "cancelled", "refunded", "revision_required"].includes(s)
        ? "bg-[#FBE9E9] text-[#B91C1C]" : "bg-[#F4EFE6] text-[#B45309]"}`}>
    {String(s || "-").replace(/_/g, " ")}
  </span>
);

export function PortalDashboard() {
  const [b, setB] = useState([]);
  const [inv, setInv] = useState([]);
  const load = useCallback(() => {
    api.get("/bookings?personal=true").then((r) => setB(r.data.items)).catch(() => {});
    api.get("/invoices?personal=true").then((r) => setInv(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const outstanding = inv.reduce((s, i) => s + (i.balance || 0), 0);
  return (
    <PortalLayout title="Dashboard Jamaah">
      <div className="grid gap-4 sm:grid-cols-3" data-testid="portal-stats">
        <Card><div className="eyebrow">Booking Aktif</div><div className="mt-2 font-display text-3xl font-bold text-[#141414]">{b.length}</div></Card>
        <Card><div className="eyebrow">Total Tagihan Tersisa</div><div className="mt-2 font-display text-2xl font-bold text-[#141414]">{rupiah(outstanding)}</div></Card>
        <Card><div className="eyebrow">Invoice</div><div className="mt-2 font-display text-3xl font-bold text-[#141414]">{inv.length}</div></Card>
      </div>
      <Card className="mt-6">
        <div className="eyebrow">Booking Terbaru</div>
        <div className="mt-4 space-y-3">
          {b.slice(0, 4).map((x) => (
            <Link key={x.id} to="/portal/booking" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] p-3 text-sm">
              <span><b>{x.booking_code}</b> — {x.product_name}</span>
              <StatusBadge s={x.status} />
            </Link>))}
          {b.length === 0 && <p className="text-sm text-[#8A867C]">Belum ada booking. <Link to="/paket/umrah-reguler" className="font-bold text-[#C5A028]">Pilih paket</Link></p>}
        </div>
      </Card>
    </PortalLayout>
  );
}

export function PortalBookings() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    api.get("/bookings?personal=true").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const open = async (id) => {
    try { const { data } = await api.get(`/bookings/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <PortalLayout title="Booking Saya">
      <div className="space-y-4" data-testid="portal-bookings">
        {items.map((b) => (
          <Card key={b.id} data-testid={`portal-booking-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow">{b.booking_code}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-[#141414]">{b.product_name}</h3>
                <div className="text-xs text-[#8A867C]">
                  {b.pax} jamaah · {b.depart_date ? tanggal(b.depart_date) : "jadwal menyusul"} · Kamar {b.room_type}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge s={b.status} />
                <div className="mt-2 text-sm font-bold text-[#141414]">{rupiah(b.total_amount)}</div>
                <div className="text-xs text-[#8A867C]">dibayar {rupiah(b.paid_amount)}</div>
              </div>
            </div>
            <button onClick={() => open(b.id)} data-testid={`portal-booking-detail-${b.id}`}
              className="mt-4 rounded-lg border border-[#141414] px-4 py-2 text-xs font-bold text-[#141414]">Lihat Detail & Timeline</button>
          </Card>))}
        {items.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada booking.</Card>}
      </div>
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <Card className="my-8 w-full max-w-2xl" data-testid="portal-booking-detail-panel">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{detail.booking_code}</div>
                <h3 className="font-display text-xl font-bold text-[#141414]">{detail.product_name}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-sm">Tutup</button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
              <div>Total: <b>{rupiah(detail.total_amount)}</b></div>
              <div>Dibayar: <b>{rupiah(detail.paid_amount)}</b></div>
              <div>Invoice: <b>{detail.invoice_number}</b></div>
              <div>Status: <StatusBadge s={detail.status} /></div>
            </div>
            <div className="mt-5 eyebrow">Riwayat Status</div>
            <ol className="mt-3 space-y-3" data-testid="booking-status-timeline">
              {(detail.status_history || []).map((h) => (
                <li key={`${h.status}-${h.at}`} className="relative border-l border-[#E5DEC9] pl-4 text-sm">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#C5A028]" />
                  <div className="font-semibold capitalize">{String(h.status).replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#8A867C]">{h.actor} · {waktu(h.at)} {h.note && `· ${h.note}`}</div>
                </li>))}
            </ol>
          </Card>
        </div>)}
    </PortalLayout>
  );
}

const PILGRIM_FIELDS = [
  { key: "full_name_passport", label: "Nama Sesuai Paspor", type: "text", required: true },
  { key: "nik", label: "NIK", type: "text" },
  { key: "gender", label: "Jenis Kelamin", type: "select", opts: ["L", "P"] },
  { key: "birth_place", label: "Tempat Lahir", type: "text" },
  { key: "birth_date", label: "Tanggal Lahir", type: "date" },
  { key: "phone", label: "No. HP", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "address", label: "Alamat", type: "text" },
  { key: "passport_number", label: "No. Paspor", type: "text" },
  { key: "passport_expiry", label: "Kadaluarsa Paspor", type: "date" },
  { key: "relation_to_customer", label: "Hubungan", type: "text" },
  { key: "room_type", label: "Tipe Kamar", type: "select", opts: ["Quad", "Triple", "Double"] },
  { key: "emergency_contact_name", label: "Kontak Darurat", type: "text" },
  { key: "emergency_contact_phone", label: "No. Kontak Darurat", type: "text" },
  { key: "special_needs", label: "Kebutuhan Khusus", type: "text" },
];

function PilgrimEditModal({ pilgrim, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name_passport: pilgrim.full_name_passport || pilgrim.full_name || "",
    nik: pilgrim.nik || "",
    gender: pilgrim.gender || "L",
    birth_place: pilgrim.birth_place || "",
    birth_date: pilgrim.birth_date || "",
    phone: pilgrim.phone || "",
    email: pilgrim.email || "",
    address: pilgrim.address || "",
    passport_number: pilgrim.passport_number || "",
    passport_expiry: pilgrim.passport_expiry || "",
    relation_to_customer: pilgrim.relation_to_customer || "diri sendiri",
    room_type: pilgrim.room_type || "Quad",
    emergency_contact_name: pilgrim.emergency_contact_name || "",
    emergency_contact_phone: pilgrim.emergency_contact_phone || "",
    special_needs: pilgrim.special_needs || "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name_passport.trim()) { toast.error("Nama paspor wajib diisi"); return; }
    setBusy(true);
    try {
      await api.put(`/pilgrims/${pilgrim.id}`, form);
      toast.success("Data jamaah berhasil diperbarui");
      onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="pilgrim-edit-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#E5DEC9] px-6 py-4">
          <h2 className="font-display text-lg font-bold text-[#141414]">Edit Data Jamaah</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[#F4EFE6]" data-testid="pilgrim-edit-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="max-h-[70vh] overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PILGRIM_FIELDS.map((f) => (
              <div key={f.key} className={f.key === "address" || f.key === "special_needs" ? "sm:col-span-2" : ""}>
                <label className={lbl}>{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</label>
                {f.type === "select" ? (
                  <select className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} data-testid={`pilgrim-field-${f.key}`}>
                    {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type} className={inp} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`pilgrim-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-xl border border-[#E5DEC9] px-5 py-2 text-sm font-semibold hover:bg-[#F4EFE6]" data-testid="pilgrim-edit-cancel">Batal</button>
            <button type="submit" disabled={busy} className="flex items-center gap-2 rounded-xl bg-[#141414] px-5 py-2 text-sm font-semibold text-white hover:bg-[#2A2A2A] disabled:opacity-50" data-testid="pilgrim-edit-save">
              <Save size={15} />{busy ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PortalPilgrims() {
  const [rows, setRows] = useState([]);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/bookings?personal=true").then(async (r) => {
      const all = [];
      for (const b of r.data.items) {
        const { data } = await api.get(`/bookings/${b.id}/pilgrims`);
        data.forEach((p) => all.push({ ...p, booking_code: b.booking_code }));
      }
      setRows(all);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PortalLayout title="Anggota Rombongan / Keluarga">
      {editTarget && (
        <PilgrimEditModal pilgrim={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />
      )}
      <div className="grid gap-4 sm:grid-cols-2" data-testid="portal-pilgrims">
        {rows.map((p) => (
          <Card key={p.id} data-testid={`portal-pilgrim-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="eyebrow">{p.booking_code}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-[#141414]">{p.full_name}</h3>
              </div>
              <button
                onClick={() => setEditTarget(p)}
                className="shrink-0 rounded-lg border border-[#E5DEC9] p-2 hover:bg-[#F4EFE6] hover:border-[#C5A028] transition-colors"
                title="Edit data jamaah"
                data-testid={`portal-pilgrim-edit-${p.id}`}
              >
                <Pencil size={15} className="text-[#C5A028]" />
              </button>
            </div>
            <div className="mt-2 space-y-1 text-sm text-[#4A4844]">
              <div>NIK: {p.nik || "-"}</div>
              <div>Paspor: {p.passport_number || "-"} {p.passport_expiry && `(s.d. ${p.passport_expiry})`}</div>
              <div>Hubungan: {p.relation_to_customer}</div>
              <div>Tipe Kamar: {p.room_type || "-"}</div>
              <div>Kebutuhan khusus: {p.special_needs || "-"}</div>
              <div className="pt-1">Dokumen: <StatusBadge s={p.document_status} /></div>
            </div>
          </Card>))}
        {rows.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada data jamaah.</Card>}
      </div>
    </PortalLayout>
  );
}

export function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/invoices?personal=true").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/payments?personal=true").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/public/settings").then(() => {}).catch(() => {});
    api.get("/bookings?personal=true").then(() => {}).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/public/banks").then((r) => setBanks(r.data)).catch(() => setBanks([]));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (k !== "file" && k !== "_inv") fd.append(k, v); });
    if (form.file) fd.append("file", form.file);
    try {
      await api.post("/payments", fd);
      toast.success("Bukti pembayaran terkirim, menunggu verifikasi Finance");
      setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const receipt = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const invoicePdf = async (i) => {
    try {
      const res = await api.get(`/invoices/${i.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PortalLayout title="Tagihan, Cicilan & Kuitansi">
      <div className="space-y-4" data-testid="portal-invoices">
        {invoices.map((i) => (
          <Card key={i.id} data-testid={`portal-invoice-${i.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow">{i.invoice_number}</div>
                <div className="mt-1 text-sm text-[#4A4844]">{i.kind === "booking" ? "Paket ibadah" : "Toko Online"}</div>
                <div className="mt-2 text-sm">Total <b>{rupiah(i.total)}</b> · Dibayar <b>{rupiah(i.paid_amount)}</b> · Sisa <b className="text-[#B45309]">{rupiah(i.balance)}</b></div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge s={i.status} />
                <div className="flex gap-2">
                  <button onClick={() => invoicePdf(i)} data-testid={`invoice-pdf-${i.id}`}
                    className="flex items-center gap-1 rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold"><Download size={12} /> Invoice PDF</button>
                  {i.balance > 0 && (
                    <button onClick={() => setForm({ invoice_id: i.id, amount: i.balance,
                      transfer_date: new Date().toISOString().slice(0, 10), sender_bank: "",
                      sender_name: "", company_bank_account_id: banks[0]?.id || "",
                      reference_no: "", note: "", file: null, _inv: i })}
                      data-testid={`pay-btn-${i.id}`} className="btn-gold flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold">
                      <Upload size={12} /> Bayar / Cicil
                    </button>)}
                </div>
              </div>
            </div>
          </Card>))}
        {invoices.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada tagihan.</Card>}
      </div>

      <h2 className="mt-8 font-display text-xl font-semibold text-[#141414]">Riwayat Cicilan</h2>
      <div className="mt-4 overflow-x-auto rounded-xl border border-[#E5DEC9] bg-white">
        <table className="w-full text-sm" data-testid="portal-payments">
          <thead className="bg-[#F4EFE6] text-left text-xs uppercase text-[#4A4844]">
            <tr><th className="p-3">Nomor</th><th className="p-3">Invoice</th><th className="p-3">Nominal</th>
              <th className="p-3">Tanggal</th><th className="p-3">Status</th><th className="p-3">Kuitansi</th></tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`portal-payment-${p.id}`}>
                <td className="p-3 font-semibold">{p.payment_number}</td>
                <td className="p-3">{p.invoice_number}</td>
                <td className="p-3">{rupiah(p.amount)}</td>
                <td className="p-3">{p.transfer_date}</td>
                <td className="p-3"><StatusBadge s={p.status} /></td>
                <td className="p-3">{p.status === "verified" && (
                  <button onClick={() => receipt(p)} data-testid={`receipt-${p.id}`}
                    className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Kuitansi PDF</button>)}
                  {p.status === "rejected" && <span className="text-xs text-[#B91C1C]">{p.rejection_reason}</span>}</td>
              </tr>))}
            {payments.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#8A867C]">Belum ada pembayaran.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <form onSubmit={submit} className="my-8 w-full max-w-lg rounded-2xl bg-white p-7" data-testid="payment-form">
            <h2 className="font-display text-xl font-bold text-[#141414]">Kirim Bukti Pembayaran</h2>
            <p className="mt-1 text-xs text-[#8A867C]">Sisa tagihan {rupiah(form._inv.balance)}. Cicilan bebas, nominal ditentukan sendiri.</p>
            <div className="mt-5 space-y-3">
              <div><label className={lbl}>Nominal transfer (IDR)</label>
                <input type="number" required className={inp} data-testid="pay-amount" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><label className={lbl}>Tanggal transfer</label>
                <input type="date" required className={inp} data-testid="pay-date" value={form.transfer_date}
                  onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} /></div>
              <div><label className={lbl}>Bank pengirim</label>
                <input required className={inp} data-testid="pay-sender-bank" value={form.sender_bank}
                  onChange={(e) => setForm({ ...form, sender_bank: e.target.value })} /></div>
              <div><label className={lbl}>Nama pengirim</label>
                <input required className={inp} data-testid="pay-sender-name" value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })} /></div>
              <div><label className={lbl}>Rekening tujuan</label>
                <select required className={inp} data-testid="pay-bank-target" value={form.company_bank_account_id}
                  onChange={(e) => setForm({ ...form, company_bank_account_id: e.target.value })}>
                  <option value="">Pilih rekening</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_number}</option>)}
                </select></div>
              <div><label className={lbl}>Nomor referensi (opsional)</label>
                <input className={inp} data-testid="pay-reference" value={form.reference_no}
                  onChange={(e) => setForm({ ...form, reference_no: e.target.value })} /></div>
              <div><label className={lbl}>Bukti transfer</label>
                <input type="file" className={inp} data-testid="pay-file"
                  onChange={(e) => setForm({ ...form, file: e.target.files[0] })} /></div>
              <div><label className={lbl}>Catatan</label>
                <input className={inp} data-testid="pay-note" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <div className="mt-6 flex gap-3">
              <button disabled={busy} data-testid="pay-submit" className="btn-gold rounded-xl px-6 py-3 text-sm font-bold">{busy ? "Mengirim..." : "Kirim Bukti"}</button>
              <button type="button" onClick={() => setForm(null)} className="rounded-xl border border-[#E5DEC9] px-6 py-3 text-sm font-semibold">Batal</button>
            </div>
          </form>
        </div>)}
    </PortalLayout>
  );
}

export function PortalDocuments() {
  const [pilgrims, setPilgrims] = useState([]);
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState(null);
  const [upload, setUpload] = useState({ document_type_id: "", file: null, note: "" });

  const loadPilgrims = useCallback(() => {
    api.get("/bookings?personal=true").then(async (r) => {
      const all = [];
      for (const b of r.data.items) {
        const { data } = await api.get(`/bookings/${b.id}/pilgrims`);
        data.forEach((p) => all.push({ ...p, booking_code: b.booking_code }));
      }
      setPilgrims(all);
      if (all[0]) setSel(all[0].id);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadPilgrims(); }, [loadPilgrims]);

  const loadDocs = useCallback(() => {
    if (!sel) return;
    api.get(`/pilgrims/${sel}/documents`).then((r) => setDocs(r.data)).catch((e) => toast.error(apiError(e)));
  }, [sel]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const send = async (e) => {
    e.preventDefault();
    if (!upload.file || !upload.document_type_id) return toast.error("Pilih jenis dokumen dan file");
    const fd = new FormData();
    fd.append("document_type_id", upload.document_type_id);
    fd.append("note", upload.note);
    fd.append("file", upload.file);
    try {
      await api.post(`/pilgrims/${sel}/documents`, fd);
      toast.success("Dokumen diunggah ke storage privat");
      setUpload({ document_type_id: "", file: null, note: "" });
      loadDocs();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const view = async (fileId) => {
    try {
      const { data } = await api.get(`/documents/${fileId}/signed-url`);
      const a = document.createElement("a");
      a.href = `${FILE_BASE}${data.signed_url}`;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PortalLayout title="Dokumen Jamaah">
      <Card>
        <label className={lbl}>Pilih jamaah</label>
        <select className={inp} data-testid="doc-pilgrim-select" value={sel || ""} onChange={(e) => setSel(e.target.value)}>
          {pilgrims.map((p) => <option key={p.id} value={p.id}>{p.full_name} ({p.booking_code})</option>)}
        </select>
        {docs && (
          <div className="mt-4 rounded-lg bg-[#FAF7F0] p-3 text-sm" data-testid="doc-completeness">
            Kelengkapan dokumen wajib: <b>{docs.completeness.approved}/{docs.completeness.required}</b> disetujui ({docs.completeness.percent}%)
          </div>)}
      </Card>

      {docs && (
        <>
          <div className="mt-6 overflow-x-auto rounded-xl border border-[#E5DEC9] bg-white">
            <table className="w-full text-sm" data-testid="doc-table">
              <thead className="bg-[#F4EFE6] text-left text-xs uppercase text-[#4A4844]">
                <tr><th className="p-3">Jenis Dokumen</th><th className="p-3">Status</th><th className="p-3">Versi</th>
                  <th className="p-3">Reviewer</th><th className="p-3">Catatan</th><th className="p-3">Aksi</th></tr>
              </thead>
              <tbody>
                {docs.documents.map((d) => (
                  <tr key={d.document_type_id} className="border-t border-[#F4EFE6]" data-testid={`doc-row-${d.document_type_id}`}>
                    <td className="p-3 font-semibold">{d.document_type_name}{d.is_required && <span className="text-[#B91C1C]"> *</span>}</td>
                    <td className="p-3"><StatusBadge s={d.status} /></td>
                    <td className="p-3">v{d.version}</td>
                    <td className="p-3">{d.reviewer || "-"}</td>
                    <td className="p-3">{d.review_note || "-"}</td>
                    <td className="p-3">{d.file_id && (
                      <button onClick={() => view(d.file_id)} data-testid={`doc-view-${d.document_type_id}`}
                        className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Lihat (signed URL)</button>)}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>

          <form onSubmit={send} className="mt-6 grid gap-3 rounded-xl border border-[#E5DEC9] bg-white p-5 sm:grid-cols-4" data-testid="doc-upload-form">
            <select className={inp} data-testid="doc-type" value={upload.document_type_id}
              onChange={(e) => setUpload({ ...upload, document_type_id: e.target.value })}>
              <option value="">Jenis dokumen</option>
              {docs.documents.map((d) => <option key={d.document_type_id} value={d.document_type_id}>{d.document_type_name}</option>)}
            </select>
            <input type="file" className={inp} data-testid="doc-file" onChange={(e) => setUpload({ ...upload, file: e.target.files[0] })} />
            <input className={inp} placeholder="Catatan" data-testid="doc-note" value={upload.note}
              onChange={(e) => setUpload({ ...upload, note: e.target.value })} />
            <button className="btn-gold rounded-lg py-2 text-sm font-bold" data-testid="doc-submit">Unggah</button>
          </form>
        </>)}
    </PortalLayout>
  );
}

export function PortalSchedule() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/bookings?personal=true").then(async (r) => {
      const all = [];
      for (const b of r.data.items) {
        const { data } = await api.get(`/bookings/${b.id}`);
        all.push(data);
      }
      setRows(all);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalLayout title="Jadwal & Itinerary">
      <div className="space-y-5" data-testid="portal-schedule">
        {rows.map((b) => (
          <Card key={b.id}>
            <div className="eyebrow">{b.booking_code}</div>
            <h3 className="mt-1 font-display text-lg font-bold text-[#141414]">{b.product_name}</h3>
            <div className="mt-1 text-sm text-[#4A4844]">
              Keberangkatan {b.depart_date ? tanggal(b.depart_date) : "-"} · Kepulangan {b.return_date ? tanggal(b.return_date) : "-"}
            </div>
            <ol className="mt-4 space-y-3">
              {(b.itineraries || []).map((it) => (
                <li key={it.id} className="rounded-lg border border-[#E5DEC9] p-3">
                  <div className="eyebrow">Hari {it.day}</div>
                  <div className="text-sm font-semibold">{it.title}</div>
                  <div className="text-xs text-[#8A867C]">{it.description}</div>
                </li>))}
              {(b.itineraries || []).length === 0 && <li className="text-sm text-[#8A867C]">Itinerary belum tersedia.</li>}
            </ol>
          </Card>))}
        {rows.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada jadwal.</Card>}
      </div>
    </PortalLayout>
  );
}

export function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => {
    api.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalLayout title="Order Toko Online">
      <div className="space-y-4" data-testid="portal-orders">
        {orders.map((o) => (
          <Card key={o.id} data-testid={`portal-order-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow">{o.order_number}</div>
                <div className="mt-1 text-sm">{o.recipient_name} · {o.shipping_city}</div>
                <ul className="mt-2 text-xs text-[#8A867C]">
                  {(o.items || []).map((i) => <li key={i.id}>{i.name} × {i.qty}</li>)}
                </ul>
              </div>
              <div className="text-right">
                <StatusBadge s={o.status} />
                <div className="mt-2 text-sm font-bold">{rupiah(o.total_amount)}</div>
                <div className="text-xs text-[#15803D]">Gratis ongkir</div>
              </div>
            </div>
          </Card>))}
        {orders.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada order.</Card>}
      </div>
    </PortalLayout>
  );
}

export function PortalNotifications() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/notifications").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalLayout title="Notifikasi">
      <div className="space-y-3" data-testid="portal-notifications">
        {rows.map((n) => (
          <Card key={n.id} data-testid={`notif-${n.id}`}>
            <div className="text-sm font-bold text-[#121212]">{n.title}</div>
            <div className="text-sm text-[#4A4844]">{n.body}</div>
            <div className="mt-1 text-xs text-[#8A867C]">{waktu(n.created_at)}</div>
          </Card>))}
        {rows.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada notifikasi.</Card>}
      </div>
    </PortalLayout>
  );
}

export function PortalProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    full_name: "", phone: "",
    address_street: "", address_city: "", address_province: "", address_postal_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      full_name: user.full_name || "",
      phone: user.phone || "",
      address_street: user.address_street || "",
      address_city: user.address_city || "",
      address_province: user.address_province || "",
      address_postal_code: user.address_postal_code || "",
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profil diperbarui");
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/storage/avatar", fd);
      setUser({ ...user, avatar_url: data.avatar_url });
      toast.success("Foto profil diperbarui");
    } catch (e2) { toast.error(apiError(e2)); } finally { setUploadingAvatar(false); e.target.value = ""; }
  };

  const reset = async () => {
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success("Tautan reset password dikirim ke email terdaftar");
    } catch (e) { toast.error(apiError(e)); }
  };

  const avatarSrc = user?.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${FILE_BASE}${user.avatar_url}`)
    : null;

  return (
    <PortalLayout title="Profil & Keamanan">
      <Card data-testid="portal-profile-avatar">
        <div className="eyebrow">Foto Profil</div>
        <div className="mt-4 flex items-center gap-5">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E5DEC9] bg-[#F4EFE6] text-2xl font-bold text-[#8A867C]">
            {avatarSrc
              ? <img src={avatarSrc} alt="Avatar" className="h-full w-full object-cover" />
              : (user?.full_name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <label className="btn-gold inline-block cursor-pointer rounded-xl px-5 py-2.5 text-sm font-bold">
              {uploadingAvatar ? "Mengunggah..." : "Ubah Foto"}
              <input type="file" accept="image/*" className="hidden" onChange={onAvatar}
                data-testid="portal-avatar-input" disabled={uploadingAvatar} />
            </label>
            <p className="mt-2 text-xs text-[#8A867C]">JPG/PNG/WEBP maks. 3MB.</p>
          </div>
        </div>
      </Card>

      <Card className="mt-5" data-testid="portal-profile-form">
        <div className="eyebrow">Data Diri</div>
        <form onSubmit={save} className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Nama Lengkap</label>
            <input required className={inp} data-testid="profile-fullname" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><label className={lbl}>Nomor WhatsApp</label>
            <input className={inp} data-testid="profile-phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Alamat Jalan</label>
            <input className={inp} data-testid="profile-address-street" value={form.address_street}
              onChange={(e) => setForm({ ...form, address_street: e.target.value })}
              placeholder="Jl. Contoh No. 123, RT/RW 001/002" /></div>
          <div><label className={lbl}>Kota / Kabupaten</label>
            <input className={inp} data-testid="profile-address-city" value={form.address_city}
              onChange={(e) => setForm({ ...form, address_city: e.target.value })} /></div>
          <div><label className={lbl}>Provinsi</label>
            <input className={inp} data-testid="profile-address-province" value={form.address_province}
              onChange={(e) => setForm({ ...form, address_province: e.target.value })} /></div>
          <div><label className={lbl}>Kode Pos</label>
            <input className={inp} data-testid="profile-address-postal" value={form.address_postal_code}
              onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })} /></div>
          <div><label className={lbl}>Email (tidak dapat diubah sendiri)</label>
            <input className={inp} value={user?.email || ""} disabled /></div>
          <div className="sm:col-span-2">
            <button disabled={busy} data-testid="profile-save-btn"
              className="btn-gold rounded-xl px-6 py-3 text-sm font-bold">
              <Save size={14} className="mr-1 inline" />{busy ? "Menyimpan..." : "Simpan Profil"}
            </button>
          </div>
        </form>
      </Card>

      <Card className="mt-5">
        <div className="eyebrow">Keamanan</div>
        <p className="mt-2 text-sm text-[#4A4844]">Password Anda disimpan dalam bentuk hash bcrypt. Gunakan tautan reset untuk mengganti password.</p>
        <button onClick={reset} data-testid="portal-reset-password" className="btn-gold mt-4 rounded-xl px-5 py-2.5 text-sm font-bold">Kirim Tautan Reset Password</button>
      </Card>

      <Card className="mt-5">
        <div className="eyebrow">Peran & Bantuan</div>
        <p className="mt-2 text-sm text-[#4A4844]">Peran: <b>{user?.role_labels?.join(", ")}</b></p>
        <Link to="/kontak" className="mt-3 inline-block rounded-xl border border-[#141414] px-5 py-2.5 text-sm font-semibold text-[#141414]">Halaman Kontak</Link>
      </Card>
    </PortalLayout>
  );
}
